import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ltiService } from '@/services/lti.service';
import type {
  MapResourceLinkRequest,
  RegisterLtiPlatformRequest,
} from '@/types/lti.types';

const LTI_KEYS = {
  platforms: ['lti', 'platforms'] as const,
  platform: (id: string) => ['lti', 'platforms', id] as const,
  jwks: ['lti', 'jwks'] as const,
  unmapped: ['lti', 'unmapped-links'] as const,
  config: ['lti', 'config'] as const,
};

/** List registered LMS platforms. */
export function useLtiPlatforms() {
  return useQuery({
    queryKey: LTI_KEYS.platforms,
    queryFn: () => ltiService.getPlatforms(),
    staleTime: 60 * 1000,
  });
}

/** Get a single platform (for the edit form). */
export function useLtiPlatform(id?: string) {
  return useQuery({
    queryKey: LTI_KEYS.platform(id || ''),
    queryFn: () => ltiService.getPlatform(id as string),
    enabled: Boolean(id),
  });
}

/** This tenant's published JWKS (for the Tool Details copy panel). */
export function useLtiJwks() {
  return useQuery({
    queryKey: LTI_KEYS.jwks,
    queryFn: () => ltiService.getJwks(),
    staleTime: 5 * 60 * 1000,
  });
}

/** Links launched from an LMS that still need mapping to Skillzen content. */
export function useUnmappedLtiLinks() {
  return useQuery({
    queryKey: LTI_KEYS.unmapped,
    queryFn: () => ltiService.getUnmappedLinks(),
    staleTime: 30 * 1000,
  });
}

export function useCreateLtiPlatform() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RegisterLtiPlatformRequest) =>
      ltiService.createPlatform(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LTI_KEYS.platforms });
      toast.success('LMS platform registered successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to register platform');
    },
  });
}

export function useUpdateLtiPlatform() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<RegisterLtiPlatformRequest>;
    }) => ltiService.updatePlatform(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: LTI_KEYS.platform(variables.id) });
      queryClient.invalidateQueries({ queryKey: LTI_KEYS.platforms });
      toast.success('Platform updated successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to update platform');
    },
  });
}

export function useSuspendLtiPlatform() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ltiService.suspendPlatform(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LTI_KEYS.platforms });
      toast.success('Platform suspended');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to suspend platform');
    },
  });
}

export function useActivateLtiPlatform() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => ltiService.activatePlatform(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LTI_KEYS.platforms });
      toast.success('Platform re-activated');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to re-activate the platform');
    },
  });
}

/** Whether LTI is switched on for this tenant. */
export function useLtiConfig() {
  return useQuery({
    queryKey: LTI_KEYS.config,
    queryFn: () => ltiService.getLtiConfig(),
  });
}

export function useSetLtiEnabled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => ltiService.setLtiEnabled(enabled),
    onSuccess: (status) => {
      queryClient.setQueryData(LTI_KEYS.config, status);
      toast.success(status.enabled ? 'LTI integration enabled' : 'LTI integration disabled');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Could not change the LTI setting');
    },
  });
}

export function useMapResourceLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MapResourceLinkRequest) =>
      ltiService.mapResourceLink(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LTI_KEYS.platforms });
      // Mapping clears the pending record server-side, so refresh that list too.
      queryClient.invalidateQueries({ queryKey: LTI_KEYS.unmapped });
      toast.success('Resource link mapped successfully');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to map resource link');
    },
  });
}
